// SliderProduto.js — Carrossel usado em "Você poderia gostar".
// Conforme mockup:
//  - Setas com background + borda + estados de hover/active
//  - Gradientes nas laterais esquerda/direita para sugerir "scroll infinito"
import React, { useState, useEffect } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { CustomPrevArrow, CustomNextArrow } from "./CustomSliderArrow.js";
import CardDestaque from "../cardDestaque/CardDestaque.js";
import CardExpansivo from "./CardExpansivo.js";
import Vitrine from "../vitrine/Vitrine";
import CardSkeleton from "../../ui/CardSkeleton";

// Embaralha uma cópia do array (Fisher-Yates) — usado na seção de recomendações
const embaralhar = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

function SliderProduto({
  nome,
  tipo,
  icone = "bi-star-fill",
  // O contexto da faixa, na moldura (ver vitrine/Vitrine.js). Quem usa a seção
  // escreve os dois: é a única forma de a frase continuar verdadeira quando a
  // mesma seção for reaproveitada para outra lista.
  chapeu,
  descricao,
  verTodos,
  endpoint,
  random = false, // quando true, exibe os livros em ordem aleatória a cada carga
  // Capa QUADRADA que se abre no hover, revelando o card inteiro por cima dos
  // vizinhos. É exclusivo de "Você poderia gostar" — nas demais faixas o card
  // é o mesmo do resto do site. Ver CardExpansivo.js e o bloco
  // ".carrossel-expansivo" no App.css.
  expansivo = false,
}) {
  const [ebooks, setEbooks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const apiUrl = process.env.REACT_APP_API;

  // Busca ebooks de acordo com o nome/endpoint da seção
  useEffect(() => {
    const fetchEbook = async () => {
      try {
        let url = apiUrl + (endpoint || "ebook");
        if (!endpoint) {
          if (nome === "Mais Recentes" || nome === "Novidades")
            url = apiUrl + "ebooks-recentes";
          else if (nome === "Mais Populares" || nome === "Mais Vendidos")
            url = apiUrl + "popular-ebooks";
        }
        const r = await fetch(url);
        if (!r.ok) throw new Error("Falha ao buscar o ebook");
        const data = await r.json();
        setEbooks(random && Array.isArray(data) ? embaralhar(data) : data);
      } catch (e) {
        console.error("Erro ao buscar o ebook:", e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchEbook();
  }, [nome, apiUrl, endpoint, random]);

  const settings = {
    dots: false,
    arrows: true,
    infinite: true,
    speed: 500,
    slidesToShow: 5,
    slidesToScroll: 1,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    responsive: [
      { breakpoint: 1280, settings: { slidesToShow: 4 } },
      { breakpoint: 1024, settings: { slidesToShow: 3 } },
      { breakpoint: 850, settings: { slidesToShow: 2 } },
      { breakpoint: 600, settings: { slidesToShow: 1 } },
    ],
  };

  // Carrossel sem livro nenhum é uma moldura em volta de nada. Some inteiro —
  // é o mesmo critério da VitrineCapas e dos destaques.
  if (!isLoading && ebooks.length === 0) return null;

  return (
    <Vitrine
      chapeu={chapeu}
      titulo={nome}
      icone={icone}
      descricao={descricao}
      verTodos={verTodos}
      className={"w-full " + (expansivo ? "secao-expansiva" : "")}
    >
      {/* Wrapper relativo: contém o carrossel + gradientes laterais.
          overflow-hidden clipa o "peek" do próximo slide (que aparecia em
          certos níveis de zoom por arredondamento de sub-pixel do react-slick).

          NA FAIXA EXPANSÍVEL A CLASSE NÃO ENTRA. Motivo: o Bootstrap (que o
          site carrega inteiro no index.js) declara
          `.overflow-hidden { overflow: hidden !important }`, e esse
          `!important` ganhava do `overflow: clip visible` do App.css por mais
          específico que o seletor fosse. Resultado: a capa crescia no hover e
          era decepada na borda de baixo — preço e carrinho sumiam.
          Tirar a classe é melhor que responder com outro `!important`: aqui
          só existe uma regra, e o recorte horizontal continua vindo do
          `.secao-expansiva .carrossel-recorte` do App.css. */}
      <div className={"carrossel-recorte relative " + (expansivo ? "" : "overflow-hidden")}>
        {/* Gradiente lateral esquerdo (fade de borda fino — só sugere "scroll",
            sem escurecer os cards totalmente visíveis). z-20 garante que fique
            acima dos slides em movimento; as setas (z-30) ficam acima dele. */}
        <div className="pointer-events-none absolute top-0 bottom-0 left-0 w-8 md:w-12 z-20
                        bg-gradient-to-r from-insigne-bg to-transparent" />
        {/* Gradiente lateral direito */}
        <div className="pointer-events-none absolute top-0 bottom-0 right-0 w-8 md:w-12 z-20
                        bg-gradient-to-l from-insigne-bg to-transparent" />

        {isLoading ? (
          // Esqueleto com o mesmo número de colunas do carrossel: sem isso,
          // a seção "encolhia" para a altura do spinner e a página saltava.
          <div className="grid grid-cols-1 min-[600px]:grid-cols-2 min-[850px]:grid-cols-3 min-[1024px]:grid-cols-4 min-[1280px]:grid-cols-5">
            {Array.from({ length: 5 }).map((_, i) => <CardSkeleton key={i} />)}
          </div>
        ) : (
          <Slider {...settings}>
            {ebooks.map((product) =>
              expansivo ? (
                // O CardExpansivo já traz o próprio bloco de espaçamento e
                // não tem botão vazando para fora, então dispensa o pb-8.
                <CardExpansivo key={product.id} livro={product} />
              ) : (
                // pb-8: o wrapper do carrossel tem overflow-hidden e cortaria
                // a metade do botão que vaza para fora do card.
                <div key={product.id} className="h-full pb-8">
                  {/* Card único do site — o mesmo das grades e dos destaques */}
                  <CardDestaque livro={product} />
                </div>
              )
            )}
          </Slider>
        )}
      </div>
    </Vitrine>
  );
}

export default SliderProduto;
